import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tooltip, Typography, useTheme,
} from '@mui/material';
import {
  RuleOutlined as GovernanceIcon, CheckCircleOutline as CleanIcon, Refresh as RefreshIcon,
  Build as RecomputeIcon,
} from '@mui/icons-material';
import { useVenue } from '../../../../context/VenueContext';
import PageHeader from '../../../../components/venue/PageHeader';
import inventoryDriftService from '../../../../services/inventoryDriftService';
import type { DriftReport, DriftGroup } from '../../../../services/inventoryDriftService';
import inventoryBalanceService from '../../../../services/inventoryBalanceService';
import { formatQty } from '../../../../utils/quantityFormat';

/**
 * INVENTORY-TRUST.1e — Inventory Balance Drift (ADMIN_ONLY).
 *
 * Read-only comparison of stored balances vs the ledger, venue-wide. Each
 * drifted (item, location) group links to a recompute action, which requires a
 * confirm dialog naming exactly what will be recomputed. Quantities only.
 */

const KIND_LABEL: Record<DriftGroup['kind'], string> = {
  MISMATCH: 'Mismatch',
  ORPHAN_BALANCE: 'Orphan balance',
  ORPHAN_GROUP: 'Missing balance',
};
const KIND_COLOR: Record<DriftGroup['kind'], 'error' | 'warning'> = {
  MISMATCH: 'error',
  ORPHAN_BALANCE: 'warning',
  ORPHAN_GROUP: 'warning',
};

interface RecomputeTarget {
  inventoryItemId: string;
  itemName: string | null;
  locationId: string | null;
  locationName: string | null;
}

const InventoryDriftPage: React.FC = () => {
  const theme = useTheme();
  const { venueId } = useVenue();

  const [report, setReport] = useState<DriftReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [confirmTarget, setConfirmTarget] = useState<RecomputeTarget | null>(null);
  const [recomputing, setRecomputing] = useState(false);
  const [snack, setSnack] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      setReport(await inventoryDriftService.getDrift(venueId));
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? 'Failed to load drift report.');
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => { load(); }, [load]);

  const runRecompute = async () => {
    if (!venueId || !confirmTarget || !confirmTarget.locationId) return;
    setRecomputing(true);
    try {
      const res = await inventoryBalanceService.recomputeBalance(venueId, confirmTarget.inventoryItemId, confirmTarget.locationId);
      setSnack(
        `Recomputed ${confirmTarget.itemName ?? 'item'} @ ${confirmTarget.locationName ?? 'location'}: ` +
        `${formatQty(res.before)} → ${formatQty(res.after)}.`,
      );
      setConfirmTarget(null);
      await load(); // refresh drift after correction
    } catch (err: any) {
      setSnack(err?.response?.data?.error?.message ?? 'Recompute failed.');
    } finally {
      setRecomputing(false);
    }
  };

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <PageHeader
        icon={<GovernanceIcon />}
        title="Balance Drift"
        subtitle="Compares stored stock balances against the movement ledger (the source of truth), venue-wide. Quantities only. Drifted groups can be recomputed from the ledger."
        actions={
          <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading} sx={{ textTransform: 'none' }}>
            Refresh
          </Button>
        }
      />

      {/* ── States ─────────────────────────────────────────────────────────── */}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
      {error && !loading && <Alert severity="error">{error}</Alert>}

      {!loading && !error && report && report.clean && (
        <Paper sx={{ p: 5, borderRadius: 2, textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <CleanIcon sx={{ fontSize: 48, color: '#1a7f37', mb: 1 }} />
          <Typography variant="h6" fontWeight={700}>No drift detected</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            The ledger and stored balances agree across all {report.summary.totalGroupsChecked.toLocaleString()} groups.
          </Typography>
        </Paper>
      )}

      {!loading && !error && report && !report.clean && (
        <>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Chip color="error" label={`${report.summary.mismatched} mismatched`} />
            <Chip color="warning" label={`${report.summary.orphanBalances} orphan balances`} />
            <Chip color="warning" label={`${report.summary.orphanGroups} missing balances`} />
            <Chip variant="outlined" label={`${report.summary.totalGroupsChecked} groups checked`} />
          </Stack>

          <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Ledger</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell align="right">Difference</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.groups.map((g, i) => (
                    <TableRow key={`${g.inventoryItemId}-${g.lotId ?? 'null'}-${g.ownershipAccountId ?? 'null'}-${i}`} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} noWrap title={g.itemName ?? g.inventoryItemId}>
                          {g.itemName ?? `(item ${g.inventoryItemId.slice(-6)})`}
                        </Typography>
                        {g.lotId && <Typography variant="caption" color="text.secondary">lot …{g.lotId.slice(-6)}</Typography>}
                      </TableCell>
                      <TableCell><Typography variant="body2" color="text.secondary">{g.locationName ?? '—'}</Typography></TableCell>
                      <TableCell><Chip size="small" color={KIND_COLOR[g.kind]} label={KIND_LABEL[g.kind]} sx={{ fontWeight: 600 }} /></TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatQty(g.ledgerQuantity)}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{g.balanceQuantity === null ? '—' : formatQty(g.balanceQuantity)}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 700, color: g.difference === 0 ? theme.palette.text.disabled : theme.palette.error.main }}>
                        {g.difference > 0 ? `+${formatQty(g.difference)}` : formatQty(g.difference)}
                      </TableCell>
                      <TableCell align="right">
                        {g.locationId ? (
                          <Button size="small" startIcon={<RecomputeIcon />}
                            onClick={() => setConfirmTarget({ inventoryItemId: g.inventoryItemId, itemName: g.itemName, locationId: g.locationId, locationName: g.locationName })}
                            sx={{ textTransform: 'none' }}>
                            Recompute
                          </Button>
                        ) : (
                          <Tooltip title="Group has no location — recompute is per item + location">
                            <span><Button size="small" disabled sx={{ textTransform: 'none' }}>Recompute</Button></span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {/* ── Recompute confirm — names exactly what will be recomputed ───────── */}
      <Dialog open={!!confirmTarget} onClose={recomputing ? undefined : () => setConfirmTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Recompute this balance?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            This recomputes the stored balance for
            <Box component="p" sx={{ my: 1.5, fontWeight: 600 }}>
              {confirmTarget?.itemName ?? 'this item'} at {confirmTarget?.locationName ?? 'this location'}
            </Box>
            from the movement ledger. Every lot/ownership group at this item + location is set to its
            true ledger sum; stray balance rows with no movements are zeroed. No other item or
            location is affected, and no cost values are changed.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmTarget(null)} disabled={recomputing} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={runRecompute} disabled={recomputing}
            startIcon={recomputing ? <CircularProgress size={16} color="inherit" /> : <RecomputeIcon />}
            sx={{ textTransform: 'none', fontWeight: 600 }}>
            Recompute
          </Button>
        </DialogActions>
      </Dialog>

      {snack && (
        <Alert severity="info" onClose={() => setSnack(null)} sx={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', boxShadow: 3, zIndex: theme.zIndex.snackbar }}>
          {snack}
        </Alert>
      )}
    </Box>
  );
};

export default InventoryDriftPage;
